import React from 'react';
import { Card, CardContent } from './ui/card';
import { Building2, TrendingUp, Cpu, MapPin } from 'lucide-react';
import { pickText, pickPlain } from '../lib/content';

export const AboutSection = ({ t, content, lang = 'sq' }) => {
  const c = content?.about || {};
  const heroC = content?.hero || {};
  const aboutTitle = pickText(c.title, lang, t.about.title);
  const aboutDescription = pickText(c.description, lang, t.about.description);
  const aboutSpecialization = pickText(c.specialization, lang, t.about.specialization);
  const card1 = pickText(c.card1_title, lang, t.about.luxury);
  const card2 = pickText(c.card2_title, lang, t.about.investment);
  const card3 = pickText(c.card3_title, lang, t.about.technology);
  const card4 = pickText(c.card4_title, lang, t.about.local);
  // Reuse hero stats (single source of truth for numbers)
  const stat1v = pickPlain(heroC.stat1_value, "20+");
  const stat1l = pickText(heroC.stat1_label, lang, t.about.experience);
  const stat2v = pickPlain(heroC.stat2_value, "500+");
  const stat2l = pickText(heroC.stat2_label, lang, t.about.properties);
  const stat3v = pickPlain(heroC.stat3_value, "1000+");
  const stat3l = pickText(heroC.stat3_label, lang, t.about.clients);

  const specializations = [
    { icon: Building2, title: card1, gradient: 'from-[#C5A05E] to-[#B39050]' },
    { icon: TrendingUp, title: card2, gradient: 'from-[#D4B06E] to-[#C5A05E]' },
    { icon: Cpu, title: card3, gradient: 'from-[#C5A05E] to-[#D4B06E]' },
    { icon: MapPin, title: card4, gradient: 'from-[#B39050] to-[#C5A05E]' }
  ];

  return (
    <section id="about" className="py-32 bg-gradient-to-b from-black via-slate-950 to-black relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#C5A05E]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#C5A05E]/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-20 space-y-6">
          <div className="inline-block px-6 py-2 bg-[#C5A05E]/10 backdrop-blur-sm border border-[#C5A05E]/30 rounded-full text-[#C5A05E] text-sm font-semibold uppercase tracking-wider mb-4 animate-fadeInUp">
            {aboutTitle}
          </div>
          <h2 className="text-5xl md:text-7xl font-bold text-white animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            {aboutSpecialization}
          </h2>
          <div className="flex items-center justify-center gap-3 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <div className="h-px w-24 bg-gradient-to-r from-transparent to-[#C5A05E]" />
            <div className="w-2 h-2 bg-[#C5A05E] rounded-full" />
            <div className="h-px w-24 bg-gradient-to-l from-transparent to-[#C5A05E]" />
          </div>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
            {aboutDescription}
          </p>
        </div>

        {/* Specializations Grid - Bold Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {specializations.map((spec, index) => (
            <div
              key={index}
              className="group relative animate-fadeInUp"
              style={{ animationDelay: `${0.4 + index * 0.1}s`, opacity: 0, animationFillMode: 'forwards' }}
            >
              {/* Glow effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${spec.gradient} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 rounded-3xl`} />
              
              <Card className="relative backdrop-blur-xl bg-white/5 border border-white/10 hover:border-[#C5A05E]/50 transition-all duration-500 hover:transform hover:scale-105 hover:-translate-y-2 overflow-hidden group">
                {/* Animated border on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#C5A05E]/50 to-transparent transform -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                
                <CardContent className="p-8 text-center space-y-4 relative z-10">
                  <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${spec.gradient} p-[2px] group-hover:scale-110 transition-transform duration-500`}>
                    <div className="w-full h-full bg-black/80 backdrop-blur-xl rounded-2xl flex items-center justify-center">
                      <spec.icon className="w-10 h-10 text-[#C5A05E]" />
                    </div>
                  </div>
                  <h4 className="font-bold text-white text-lg group-hover:text-[#C5A05E] transition-colors duration-300">{spec.title}</h4>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>

        {/* Stats - Premium Glass Design */}
        <div className="relative animate-fadeInUp" style={{ animationDelay: '0.8s', opacity: 0, animationFillMode: 'forwards' }}>
          {/* Glowing background */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#C5A05E]/20 via-[#D4B06E]/20 to-[#C5A05E]/20 rounded-3xl blur-2xl" />
          
          <Card className="relative backdrop-blur-2xl bg-white/5 border border-white/10 overflow-hidden">
            {/* Animated gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent transform -translate-x-full animate-shimmer" 
              style={{ animation: 'shimmer 3s infinite' }} />
            
            <CardContent className="p-12 md:p-16">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                {[
                  { value: stat1v, label: stat1l, delay: '0.2s' },
                  { value: stat2v, label: stat2l, delay: '0.4s' },
                  { value: stat3v, label: stat3l, delay: '0.6s' }
                ].map((stat, index) => (
                  <div key={index} className="text-center space-y-4 group cursor-pointer">
                    <div className="relative inline-block">
                      {/* Glowing effect behind number */}
                      <div className="absolute inset-0 bg-[#C5A05E]/30 blur-2xl group-hover:bg-[#C5A05E]/50 transition-all duration-500" />
                      <div className="relative text-6xl md:text-7xl font-bold bg-gradient-to-br from-[#C5A05E] via-[#D4B06E] to-[#C5A05E] bg-clip-text text-transparent group-hover:scale-110 transition-transform duration-500">
                        {stat.value}
                      </div>
                    </div>
                    <div className="text-gray-400 font-medium uppercase tracking-wider text-sm group-hover:text-[#C5A05E] transition-colors duration-300">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};